import type { ZodType } from "zod";
import z from "zod";

import {
  FormProgressSchema,
  type FormProgressUnbranded,
} from "../collectors/form/draft.js";
import {
  AssessmentIdSchema,
  FinishedAssessmentStatusSchema,
  type AssessmentIdUnbranded,
  type FinishedAssessmentStatus,
} from "./assessment.js";
import {
  PersonInvolvementIdSchema,
  type PersonInvolvementIdUnbranded,
} from "./involvement.js";
import { JobTitleSchema, type JobTitleUnbranded } from "./standing.js";
import {
  EmailAddressSchema,
  UserIdSchema,
  type EmailAddressUnbranded,
  type UserIdUnbranded,
} from "./user.js";

/**
 * What a guest was given, as their row on Case involvement counts it: the
 * assessments shared with them to read that still stand, or the one they were
 * asked to complete, with when they sent it, or `null` until they do.
 */
export type GuestAccessGivenUnbranded =
  | { kind: "read_shared_assessments"; assessmentCount: number }
  | { kind: "complete_assessment"; sentAt: Date | null };

export const GuestAccessGivenSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("read_shared_assessments"),
    assessmentCount: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("complete_assessment"),
    sentAt: z.coerce.date().nullable(),
  }),
]) satisfies ZodType<GuestAccessGivenUnbranded>;

export type GuestAccessGiven = z.infer<typeof GuestAccessGivenSchema>;

/**
 * One guest on a person's case, as everybody who sees Case involvement
 * sees them: who they are, what they were given, and when their access
 * started and ends. Nothing here reaches them, so their address is left to
 * `GuestOnCaseDetails`.
 *
 * `startedByUserId` is whoever added the guest.
 */
export type GuestOnCaseUnbranded = {
  involvementId: PersonInvolvementIdUnbranded;
  name: string;
  jobTitle: JobTitleUnbranded | null;
  startedByUserId: UserIdUnbranded;
  startedAt: Date;
  expiresAt: Date | null;
  endedAt: Date | null;
  access: GuestAccessGivenUnbranded;
};

export const GuestOnCaseSchema = z.object({
  involvementId: PersonInvolvementIdSchema,
  name: z.string(),
  jobTitle: JobTitleSchema.nullable(),
  startedByUserId: UserIdSchema,
  startedAt: z.coerce.date(),
  expiresAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
  access: GuestAccessGivenSchema,
}) satisfies ZodType<GuestOnCaseUnbranded>;

export type GuestOnCase = z.infer<typeof GuestOnCaseSchema>;

/** Every guest a person's case has had, ended ones included, earliest added first. */
export type GuestsOnCaseUnbranded = { guests: GuestOnCaseUnbranded[] };

export const GuestsOnCaseSchema = z.object({
  guests: z.array(GuestOnCaseSchema),
}) satisfies ZodType<GuestsOnCaseUnbranded>;

export type GuestsOnCase = z.infer<typeof GuestsOnCaseSchema>;

/**
 * One assessment shared with a guest, as their Details lists it: when it
 * finished, how, and when the guest first opened it, or `null` where they
 * have not.
 */
export type SharedAssessmentOpeningUnbranded = {
  assessmentId: AssessmentIdUnbranded;
  status: FinishedAssessmentStatus;
  finishedAt: Date;
  firstOpenedAt: Date | null;
};

const SharedAssessmentOpeningSchema = z.object({
  assessmentId: AssessmentIdSchema,
  status: FinishedAssessmentStatusSchema,
  finishedAt: z.coerce.date(),
  firstOpenedAt: z.coerce.date().nullable(),
}) satisfies ZodType<SharedAssessmentOpeningUnbranded>;

/**
 * What a guest has done with what they were given, as their Details shows it.
 *
 * For a guest reading shared assessments, each one still standing, most
 * recently finished first. For a guest asked to complete one, its id, how far
 * it has got, or `null` once it is deleted, the message sent with the
 * request, and when the guest sent it, or `null` until they do.
 */
export type GuestUseUnbranded =
  | {
      kind: "read_shared_assessments";
      assessments: SharedAssessmentOpeningUnbranded[];
    }
  | {
      kind: "complete_assessment";
      assessmentId: AssessmentIdUnbranded;
      progress: FormProgressUnbranded | null;
      message: string | null;
      sentAt: Date | null;
    };

const GuestUseSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("read_shared_assessments"),
    assessments: z.array(SharedAssessmentOpeningSchema),
  }),
  z.object({
    kind: z.literal("complete_assessment"),
    assessmentId: AssessmentIdSchema,
    progress: FormProgressSchema.nullable(),
    message: z.string().nullable(),
    sentAt: z.coerce.date().nullable(),
  }),
]) satisfies ZodType<GuestUseUnbranded>;

/** The first and last time a guest signed in through their link. */
export type GuestSignInsUnbranded = { first: Date; last: Date };

const GuestSignInsSchema = z.object({
  first: z.coerce.date(),
  last: z.coerce.date(),
}) satisfies ZodType<GuestSignInsUnbranded>;

/**
 * A guest on a person's case as their Details shows them, to those
 * `mayReadGuestDetails` allows: the address their link was sent to, who
 * added them, when they signed in, or `null` where they never have, and what
 * they have done with what they were given.
 */
export type GuestOnCaseDetailsUnbranded = {
  involvementId: PersonInvolvementIdUnbranded;
  name: string;
  jobTitle: JobTitleUnbranded | null;
  email: EmailAddressUnbranded;
  startedByUserName: string;
  startedAt: Date;
  expiresAt: Date | null;
  endedAt: Date | null;
  signIns: GuestSignInsUnbranded | null;
  use: GuestUseUnbranded;
};

export const GuestOnCaseDetailsSchema = z.object({
  involvementId: PersonInvolvementIdSchema,
  name: z.string(),
  jobTitle: JobTitleSchema.nullable(),
  email: EmailAddressSchema,
  startedByUserName: z.string(),
  startedAt: z.coerce.date(),
  expiresAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
  signIns: GuestSignInsSchema.nullable(),
  use: GuestUseSchema,
}) satisfies ZodType<GuestOnCaseDetailsUnbranded>;

export type GuestOnCaseDetails = z.infer<typeof GuestOnCaseDetailsSchema>;
